/**
 * Per-row identity actions dropdown — sits in the rightmost cell next to
 * the existing "View" / "Resend welcome" / "Mark exited" controls.
 *
 * Lets the user reclassify a single identity, archive/unarchive,
 * ignore/unignore, and accept the heuristic suggestion (when present)
 * in one click.
 */

import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { MoreHorizontal, Archive, ArchiveRestore, EyeOff, Eye, Sparkles, Trash2, Loader2, ShieldAlert, Ban, RotateCcw, Pencil, UserCog, Mail, UserX, UserCheck } from 'lucide-react';
import { useAuth } from '../App';

const ROLES = [
  { value: 'owner',    label: 'Owner' },
  { value: 'admin',    label: 'Admin' },
  { value: 'employee', label: 'Employee' },
];

const TYPES = [
  { value: 'employee',          label: 'Employee' },
  { value: 'non_employee',      label: 'Non-Employee' },
  { value: 'distribution_list', label: 'Distribution List' },
];

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

export default function IdentityActionsMenu({ employee, onAfterMutate, onEdit, onChangeRole, onResendWelcome, onMarkStatus }) {
  const [busy, setBusy] = useState(false);
  // Permanent-delete confirmation modal state. Kept tightly scoped to
  // this component so each row's modal is independent and a stale-click
  // can't trigger a delete on the wrong row.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  const { currentWorkspace } = useAuth() || {};
  const isSuperAdmin = !!(
    currentWorkspace?.is_super_admin
    || currentWorkspace?.role === 'super_admin'
  );

  const isArchived = !!employee.is_archived;
  const isSuspended = !!employee.is_suspended;
  const suggestion = employee.suggested_identity_type;
  // Suspend gating: only Owner or Super Admin sees Suspend/Unsuspend.
  // Admin role is intentionally not enough — matches backend permission
  // check in identity_routes.suspend_employee.
  const userRole = (currentWorkspace?.role || '').toLowerCase();
  const canSuspend = isSuperAdmin || userRole === 'owner';
  // Permanent-delete eligibility — backend computes this from
  // archived_at on the classification overlay so the gate is single-source.
  const daysArchived = employee.days_archived;
  const eligibleForDelete = !!employee.eligible_for_permanent_delete;
  const daysUntilEligible = isArchived && typeof daysArchived === 'number'
    ? Math.max(0, Math.ceil(7 - daysArchived))
    : 7;

  const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const refKey = encodeURIComponent(employee.email || employee.id);

  const performPermanentDelete = async () => {
    if (!reason.trim()) {
      toast.error('A reason is required for permanent deletion.');
      return;
    }
    if (confirmEmail.trim().toLowerCase() !== (employee.email || '').toLowerCase()) {
      toast.error('Confirmation email does not match.');
      return;
    }
    setDeleting(true);
    try {
      await axios.post(
        `${API}/employees/${refKey}/permanent-delete`,
        { reason: reason.trim(), confirm_email: employee.email },
        { headers: authHeader() },
      );
      toast.success(`Permanently deleted ${employee.email}. Audit log preserved.`, { duration: 6000 });
      setDeleteOpen(false);
      setReason('');
      setConfirmEmail('');
      onAfterMutate && onAfterMutate();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Permanent delete failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg), { duration: 8000 });
    } finally {
      setDeleting(false);
    }
  };

  const call = async (verb, body, label) => {
    setBusy(true);
    try {
      let res;
      if (verb === 'identity') {
        res = await axios.patch(`${API}/employees/${refKey}/identity`, body, { headers: authHeader() });
      } else {
        res = await axios.post(`${API}/employees/${refKey}/${verb}`, body, { headers: authHeader() });
      }
      // The classify / archive / ignore endpoints return invite-queue
      // side-effect counts so we can surface them on the toast (better
      // than silently doing magic behind the user's back).
      const data = res?.data || {};
      const revoked = Number(data.revoked_invite_count || 0);
      const enqueued = Boolean(data.enqueued_invite);
      const extras = [];
      if (revoked > 0) extras.push(`${revoked} pending invite${revoked === 1 ? '' : 's'} revoked`);
      if (enqueued) extras.push('queued for invite approval');
      const finalLabel = extras.length ? `${label} · ${extras.join(' · ')}` : label;
      toast.success(finalLabel);
      onAfterMutate && onAfterMutate();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${label} failed`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          data-testid={`identity-actions-trigger-${employee.id}`}
          aria-label="Identity actions"
          className="px-2"
        >
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">Identity actions</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {suggestion && (
          <>
            <DropdownMenuItem
              onClick={() => call('identity', { identity_type: suggestion, reason: 'accepted suggestion' }, 'Suggestion accepted')}
              data-testid={`identity-accept-suggestion-${employee.id}`}
              className="text-violet-700 focus:text-violet-800 focus:bg-violet-50"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Accept: {suggestion.replace('_', ' ')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {onEdit && (
          <DropdownMenuItem onClick={onEdit} data-testid={`identity-edit-${employee.id}`}>
            <Pencil className="w-4 h-4 mr-2" /> Edit
          </DropdownMenuItem>
        )}

        {onChangeRole && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid={`identity-change-role-trigger-${employee.id}`}>
              <UserCog className="w-4 h-4 mr-2" /> Change role
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-44">
              {ROLES.map((r) => (
                <DropdownMenuItem
                  key={r.value}
                  onClick={() => onChangeRole(r.value)}
                  data-testid={`identity-change-role-${r.value}-${employee.id}`}
                  className={(employee.role || '').toLowerCase() === r.value ? 'font-semibold text-emerald-700' : ''}
                >
                  {r.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        <DropdownMenuSub>
          <DropdownMenuSubTrigger data-testid={`identity-mark-as-trigger-${employee.id}`}>
            Mark identity type
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-48">
            {TYPES.map((t) => (
              <DropdownMenuItem
                key={t.value}
                onClick={() => call('identity', { identity_type: t.value }, `Marked as ${t.label}`)}
                data-testid={`identity-mark-as-${t.value}-${employee.id}`}
                className={employee.identity_type === t.value ? 'font-semibold text-emerald-700' : ''}
              >
                {t.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {onMarkStatus && employee.status === 'active' && (
          <DropdownMenuItem
            onClick={() => onMarkStatus('exited')}
            data-testid={`identity-mark-exited-${employee.id}`}
            className="text-red-700 focus:text-red-800 focus:bg-red-50"
          >
            <UserX className="w-4 h-4 mr-2" /> Mark as exited
          </DropdownMenuItem>
        )}
        {onMarkStatus && employee.status === 'exited' && (
          <DropdownMenuItem
            onClick={() => onMarkStatus('active')}
            data-testid={`identity-mark-active-${employee.id}`}
            className="text-emerald-700 focus:text-emerald-800 focus:bg-emerald-50"
          >
            <UserCheck className="w-4 h-4 mr-2" /> Mark as active
          </DropdownMenuItem>
        )}
        {onResendWelcome && (
          <DropdownMenuItem
            onClick={onResendWelcome}
            data-testid={`identity-resend-welcome-${employee.id}`}
          >
            <Mail className="w-4 h-4 mr-2" /> Resend invite
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {isArchived ? (
          <DropdownMenuItem
            onClick={() => call('unarchive', {}, 'Unarchived')}
            data-testid={`identity-unarchive-${employee.id}`}
          >
            <ArchiveRestore className="w-4 h-4 mr-2" /> Unarchive
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={() => call('archive', {}, 'Archived')}
            data-testid={`identity-archive-${employee.id}`}
          >
            <Archive className="w-4 h-4 mr-2" /> Archive
          </DropdownMenuItem>
        )}

        {/* Suspend / Unsuspend — Feb 2026.
            Disables Nova app login WITHOUT deleting the employee record.
            Owner or Super Admin only; backend re-enforces the gate. We
            keep the menu items rendered (rather than hiding) so the
            audit-side admin still has the option visible. The backend
            also blocks self-suspend and other-owner-suspend.
            Archive vs Suspend: archive removes from operational workflow;
            suspend preserves the row but blocks auth on next request. */}
        {canSuspend && (
          <>
            <DropdownMenuSeparator />
            {isSuspended ? (
              <DropdownMenuItem
                onClick={() => call('unsuspend', {}, 'Account unsuspended — access restored')}
                data-testid={`identity-unsuspend-${employee.id}`}
                className="text-amber-700 focus:text-amber-800 focus:bg-amber-50"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Unsuspend (restore access)
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={() => call('suspend', {}, 'Account suspended — login blocked')}
                data-testid={`identity-suspend-${employee.id}`}
                className="text-amber-700 focus:text-amber-800 focus:bg-amber-50"
              >
                <Ban className="w-4 h-4 mr-2" /> Suspend access
              </DropdownMenuItem>
            )}
          </>
        )}

        {/* Permanent delete — Feb 2026 lifecycle.
            • Only ever shown when the row is archived (we never offer
              destructive ops on live identities).
            • Only super admins see the actionable variant; everyone else
              sees nothing extra (keeps the menu uncluttered).
            • While inside the 7-day cool-off, the item is rendered
              disabled with a "available in N days" hint so super admins
              know the lock is intentional, not a bug. */}
        {isArchived && isSuperAdmin && (
          <>
            <DropdownMenuSeparator />
            {eligibleForDelete ? (
              <DropdownMenuItem
                onClick={() => { setConfirmEmail(''); setReason(''); setDeleteOpen(true); }}
                data-testid={`identity-delete-permanent-${employee.id}`}
                className="text-red-700 focus:text-red-800 focus:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete permanently…
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                disabled
                data-testid={`identity-delete-permanent-locked-${employee.id}`}
                className="text-slate-400 cursor-not-allowed"
                onSelect={(e) => e.preventDefault()}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete permanently · in {daysUntilEligible} day{daysUntilEligible === 1 ? '' : 's'}
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>

    {/* Confirmation modal. Two-factor manual confirmation:
        (a) re-type the email exactly,
        (b) supply a free-text reason — written to the immutable audit
        log so the action is defensible after the fact. */}
    <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
      <DialogContent className="sm:max-w-md" data-testid={`identity-delete-dialog-${employee.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <ShieldAlert className="w-5 h-5" />
            Permanently delete identity
          </DialogTitle>
          <DialogDescription>
            This permanently removes <strong className="text-slate-900">{employee.email}</strong>{' '}
            from this workspace. Archived for{' '}
            <strong>{typeof daysArchived === 'number' ? `${daysArchived} day${daysArchived === 1 ? '' : 's'}` : '—'}</strong>.
            <span className="block mt-2 text-amber-700 text-xs">
              The audit log, evidence history, and posture findings linked to this
              identity are <strong>preserved</strong> — only the membership, directory
              record, and classification overlay are removed. This cannot be undone.
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor={`del-confirm-${employee.id}`} className="text-xs">
              Re-type the email to confirm: <span className="font-mono text-slate-700">{employee.email}</span>
            </Label>
            <Input
              id={`del-confirm-${employee.id}`}
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={employee.email}
              className="mt-1 font-mono"
              autoComplete="off"
              data-testid="identity-delete-confirm-input"
            />
          </div>
          <div>
            <Label htmlFor={`del-reason-${employee.id}`} className="text-xs">
              Reason (written to audit log) *
            </Label>
            <Textarea
              id={`del-reason-${employee.id}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Off-boarded — 30-day retention complete. Confirmed with HR ticket #1234."
              className="mt-1"
              data-testid="identity-delete-reason-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setDeleteOpen(false)}
            disabled={deleting}
            data-testid="identity-delete-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={performPermanentDelete}
            disabled={
              deleting
              || !reason.trim()
              || confirmEmail.trim().toLowerCase() !== (employee.email || '').toLowerCase()
            }
            className="bg-red-600 hover:bg-red-700"
            data-testid="identity-delete-confirm-btn"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Permanently delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
